import React, { useState } from 'react';
import Modal from './Modal';
import api from '../services/api';

interface Contrato {
  contrato?: number;        // alguns retornos usam 'contrato'
  id?: number;              // fallback possível
  numeroContrato?: number;  // outro fallback possível
  contratoId?: number;      // formato consultacliente
  dataCadastro?: string;
  status?: string;
  servicos?: any[];
}

interface Cliente {
  nome: string;
  cpfcnpj: string;
  contratos: Contrato[];
}

interface VincularCpfModalProps {
  isOpen: boolean;
  onClose: () => void;
  onVincular: (cpfcnpj: string, contrato: string, clienteData: any) => void;
  numeroLinha: string;
}

const VincularCpfModal: React.FC<VincularCpfModalProps> = ({
  isOpen,
  onClose,
  onVincular,
  numeroLinha,
}) => {
  const [cpfcnpj, setCpfcnpj] = useState('');
  const [loading, setLoading] = useState(false);
  const [clienteData, setClienteData] = useState<Cliente | null>(null);
  const [contratoSelecionado, setContratoSelecionado] = useState<string>('');
  const [error, setError] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [valorMensal, setValorMensal] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [gigas, setGigas] = useState('');
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [etapa, setEtapa] = useState<1 | 2 | 3>(1); // 1 = CPF, 2 = Contrato, 3 = Valor/Gigas

  const formatCpfCnpj = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  };

  const handleBuscarCliente = async () => {
    if (!cpfcnpj) {
      setError('Digite um CPF/CNPJ');
      return;
    }

    setLoading(true);
    setError('');
    setClienteData(null);
    setContratoSelecionado('');

    try {
      const response = await api.fetchClientData(cpfcnpj);
      // A API pode retornar { clientes: [{ contratos, nome, cpfcnpj }] }
      // ou repassar diretamente { contratos, razaoSocial, cpfCnpj }.
      const resolvedCliente = response?.clientes?.[0] ?? response;
      const contratos = resolvedCliente?.contratos ?? response?.contratos ?? [];
      if (Array.isArray(contratos) && contratos.length > 0) {
        const nome = resolvedCliente?.nome || resolvedCliente?.razaoSocial || 'Cliente';
        const cpfMasked = resolvedCliente?.cpfcnpj || resolvedCliente?.cpfCnpj || cpfcnpj;
        setClienteData({
          nome,
          cpfcnpj: cpfMasked,
          contratos,
        });
      } else {
        setError('Cliente não encontrado');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao buscar cliente');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmar = () => {
    if (!contratoSelecionado || !clienteData) {
      setError('Selecione um contrato');
      return;
    }

    // Validar campos de valor e gigas
    const valor = parseFloat(valorMensal);
    const gigasNum = parseFloat(gigas);

    if (!valorMensal || isNaN(valor) || valor < 0) {
      setError('Digite um valor mensal válido');
      return;
    }

    if (!gigas || isNaN(gigasNum) || gigasNum < 0) {
      setError('Digite uma quantidade de gigas válida');
      return;
    }

    // Passar os dados junto com valor mensal e gigas
    onVincular(clienteData.cpfcnpj, contratoSelecionado, {
      ...clienteData,
      valorMensalAdicionado: valor,
      gigasAdicionado: gigasNum,
    });
    handleClose();
  };

  const handleClose = () => {
    setCpfcnpj('');
    setClienteData(null);
    setContratoSelecionado('');
    setError('');
    setValorMensal('');
    setGigas('');
    setEtapa(1);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Vincular CPF/CNPJ - Linha ${numeroLinha}`}>
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        
        {/* Indicador de Etapas */}
        <div className="flex items-center justify-between mb-6">
          {/* Etapa 1 */}
          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${etapa >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              1
            </div>
            <p className={`text-sm font-medium ${etapa >= 1 ? 'text-purple-600' : 'text-gray-500'}`}>
              CPF
            </p>
            <div className={`flex-1 h-1 mx-2 ${etapa >= 2 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
          </div>
          
          {/* Etapa 2 */}
          <div className="flex items-center gap-2 flex-1">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${etapa >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              2
            </div>
            <p className={`text-sm font-medium ${etapa >= 2 ? 'text-purple-600' : 'text-gray-500'}`}>
              Contrato
            </p>
            <div className={`flex-1 h-1 mx-2 ${etapa >= 3 ? 'bg-purple-600' : 'bg-gray-300'}`}></div>
          </div>
          
          {/* Etapa 3 */}
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-full flex items-center justify-center font-semibold text-sm ${etapa >= 3 ? 'bg-purple-600 text-white' : 'bg-gray-300 text-gray-600'}`}>
              3
            </div>
            <p className={`text-sm font-medium ${etapa >= 3 ? 'text-purple-600' : 'text-gray-500'}`}>
              Detalhes
            </p>
          </div>
        </div>

        {/* ETAPA 1: Buscar CPF */}
        {etapa === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                CPF/CNPJ do Cliente
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={cpfcnpj}
                  onChange={(e) => setCpfcnpj(formatCpfCnpj(e.target.value))}
                  placeholder="000.000.000-00"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={loading}
                />
                <button
                  onClick={handleBuscarCliente}
                  disabled={loading}
                  className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 font-medium"
                >
                  {loading ? 'Buscando...' : 'Buscar'}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Dados do Cliente */}
            {clienteData && (
              <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-lg text-gray-800 mb-1">{clienteData.nome}</h3>
                <p className="text-sm text-gray-600">CPF/CNPJ: {clienteData.cpfcnpj}</p>
              </div>
            )}

            {/* Botões Etapa 1 */}
            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                onClick={handleClose}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (!clienteData) {
                    setError('Digite um CPF/CNPJ e clique em Buscar');
                    return;
                  }
                  setError('');
                  setEtapa(2);
                }}
                disabled={!clienteData || loading}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 font-medium"
              >
                Próximo
              </button>
            </div>
          </div>
        )}

        {/* ETAPA 2: Selecionar Contrato */}
        {etapa === 2 && (
          <div className="space-y-4">
            {/* Resumo do Cliente */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
              <h3 className="font-semibold text-lg text-gray-800 mb-1">{clienteData?.nome}</h3>
              <p className="text-sm text-gray-600">CPF/CNPJ: {clienteData?.cpfcnpj}</p>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Seleção de Contrato */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Selecione o Contrato ({clienteData?.contratos.length})
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto pr-2">
                {clienteData?.contratos.map((contrato, idx) => {
                  const numeroContrato = contrato.contrato ?? contrato.contratoId ?? contrato.id ?? contrato.numeroContrato;
                  const value = numeroContrato !== undefined ? String(numeroContrato) : '';
                  const checked = contratoSelecionado === value;
                  return (
                    <label
                      key={`${clienteData?.cpfcnpj}-${value || idx}`}
                      className={`p-3 border-2 rounded-lg cursor-pointer transition-all flex items-start gap-3 ${
                        checked
                          ? 'border-purple-600 bg-purple-50 shadow-md'
                          : 'border-gray-200 hover:border-purple-300 bg-white'
                      }`}
                    >
                      <input
                        type="radio"
                        name="contrato"
                        value={value}
                        checked={checked}
                        onChange={() => value && setContratoSelecionado(value)}
                        disabled={!value}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <p className="font-semibold text-gray-800">Contrato #{numeroContrato ?? '—'}</p>
                        {contrato.status && (
                          <p className="text-xs text-gray-600 mt-1">Status: {contrato.status}</p>
                        )}
                        {contrato.dataCadastro && (
                          <p className="text-xs text-gray-600">
                            Cadastro: {new Date(contrato.dataCadastro).toLocaleDateString('pt-BR')}
                          </p>
                        )}
                        {Array.isArray(contrato.servicos) && contrato.servicos.length > 0 && (
                          <p className="text-xs text-purple-600 font-semibold mt-1">
                            Plano: {contrato.servicos[0]?.plano?.descricao || 'N/A'}
                          </p>
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Botões Etapa 2 */}
            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setError('');
                  setEtapa(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
              >
                Voltar
              </button>
              <button
                onClick={() => {
                  if (!contratoSelecionado) {
                    setError('Selecione um contrato');
                    return;
                  }
                  setError('');
                  setEtapa(3);
                }}
                disabled={!contratoSelecionado}
                className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:bg-gray-400 font-medium"
              >
                Próximo
              </button>
            </div>
          </div>
        )}

        {/* ETAPA 3: Valor Mensal e Gigas */}
        {etapa === 3 && (
          <div className="space-y-4">
            {/* Resumo do Contrato */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-gray-800 mb-2">Cliente: {clienteData?.nome}</h3>
              <p className="text-sm text-gray-600">Contrato: #{contratoSelecionado}</p>
              {clienteData?.contratos.find((c) => {
                const num = c.contrato ?? c.contratoId ?? c.id ?? c.numeroContrato;
                return String(num) === contratoSelecionado;
              })?.servicos?.[0]?.plano?.descricao && (
                <p className="text-sm text-purple-600 font-semibold mt-1">
                  Plano SGP: {clienteData?.contratos.find((c) => {
                    const num = c.contrato ?? c.contratoId ?? c.id ?? c.numeroContrato;
                    return String(num) === contratoSelecionado;
                  })?.servicos?.[0]?.plano?.descricao}
                </p>
              )}
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Formulário de Detalhes */}
            <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
              <p className="text-sm font-semibold text-gray-700 mb-4">Informações da Linha</p>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  💰 Valor Mensal (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={valorMensal}
                  onChange={(e) => setValorMensal(e.target.value)}
                  placeholder="Ex: 30.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  📡 Gigas Disponíveis (GB)
                </label>
                <input
                  type="number"
                  step="1"
                  value={gigas}
                  onChange={(e) => setGigas(e.target.value)}
                  placeholder="Ex: 100"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Botões Etapa 3 */}
            <div className="flex gap-2 justify-end pt-4 border-t">
              <button
                onClick={() => {
                  setError('');
                  setValorMensal('');
                  setGigas('');
                  setEtapa(2);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 font-medium text-gray-700"
              >
                Voltar
              </button>
              <button
                onClick={handleConfirmar}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 font-medium"
              >
                {loading ? 'Vinculando...' : 'Confirmar Vinculação'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default VincularCpfModal;
